import type {DiscoveryMatchedProcess, DiscoverySourceDocument, ProcessSuggestion} from "../../types";

export const DISCOVERY_SOURCE_DOCUMENTS: DiscoverySourceDocument[] = [
  {id: "doc-1", name: "Corporate Policy Manual v4.2", type: "PDF", citationCount: 8},
  {id: "doc-2", name: "Chart of Accounts — FY2026", type: "Spreadsheet", citationCount: 5},
  {id: "doc-3", name: "Annual Report FY2025", type: "PDF", citationCount: 4},
  {id: "doc-4", name: "IT Systems Inventory", type: "Spreadsheet", citationCount: 6},
  {id: "doc-5", name: "Internal Audit Plan — Q1 2026", type: "PDF", citationCount: 3},
  {id: "doc-6", name: "Board Risk Committee Minutes — Feb 2026", type: "PDF", citationCount: 2},
];

export const DISCOVERY_SUGGESTIONS: ProcessSuggestion[] = [
  {
    id: "suggestion-1",
    name: "Payroll processing & disbursement",
    description:
      "Salary computation, tax withholding, benefit deductions, and net pay disbursement via bi-weekly payroll cycle.",
    fsaIds: ["expenditures"],
    sourceEvidence: [
      {
        documentName: "Corporate Policy Manual v4.2",
        quote:
          "All payroll disbursements require dual authorisation for amounts exceeding $50,000. The payroll cycle runs bi-weekly with a two-day processing window.",
        pageRef: "p. 34",
      },
      {
        documentName: "Chart of Accounts — FY2026",
        quote:
          "Account codes 6100–6199 are reserved for payroll-related expenses including salaries, wages, employer taxes, and benefit contributions.",
        pageRef: "Sheet: Expense Codes",
      },
      {
        documentName: "IT Systems Inventory",
        quote: "ADP Workforce Now — primary HRIS and payroll platform, integrated with NetSuite GL via API.",
        pageRef: "Row 12",
      },
    ],
  },
  {
    id: "suggestion-2",
    name: "Fixed asset capitalisation & depreciation",
    description:
      "Lifecycle management of PP&E from acquisition through disposal, including capitalisation threshold assessment and monthly depreciation.",
    fsaIds: ["financial-reporting"],
    sourceEvidence: [
      {
        documentName: "Corporate Policy Manual v4.2",
        quote:
          "Assets with a cost exceeding $5,000 and useful life greater than 12 months must be capitalised. Depreciation commences in the month following acquisition.",
        pageRef: "p. 41",
      },
      {
        documentName: "Annual Report FY2025",
        quote:
          "Property, plant and equipment totalled $24.3M at year-end, with $3.1M in additions and $1.8M in depreciation expense recognised during the period.",
        pageRef: "Note 8",
      },
    ],
  },
  {
    id: "suggestion-3",
    name: "Treasury & cash management",
    description:
      "Daily cash positioning, 13-week rolling forecasts, bank reconciliation, and surplus cash deployment per investment policy.",
    fsaIds: ["financial-reporting"],
    sourceEvidence: [
      {
        documentName: "Board Risk Committee Minutes — Feb 2026",
        quote:
          "The Treasurer presented the updated cash management framework including revised counterparty limits and the new automated bank reconciliation tool.",
      },
      {
        documentName: "IT Systems Inventory",
        quote: "Kyriba TMS deployed Q3 2025 for cash positioning, forecasting, and bank connectivity.",
        pageRef: "Row 8",
      },
    ],
  },
  {
    id: "suggestion-4",
    name: "Inventory valuation & costing",
    description:
      "Standard cost maintenance, purchase price and production variance analysis, cycle counting, and NRV assessment for slow-moving items.",
    fsaIds: ["expenditures", "revenue"],
    sourceEvidence: [
      {
        documentName: "Annual Report FY2025",
        quote:
          "Inventories are stated at the lower of cost and net realisable value. Cost is determined using the weighted average method.",
        pageRef: "Note 2(e)",
      },
    ],
  },
];

export const DISCOVERY_MATCHED_PROCESSES: DiscoveryMatchedProcess[] = [
  {
    processId: "proc-vendor-invoice",
    confirmationNote:
      "Policy Manual describes three-way matching requirements consistent with the documented process. Chart of Accounts confirms AP account structure.",
    sourceEvidence: [
      {
        documentName: "Corporate Policy Manual v4.2",
        quote:
          "All vendor invoices must be matched against the corresponding purchase order and goods receipt note prior to payment approval.",
        pageRef: "p. 28",
      },
    ],
  },
  {
    processId: "proc-expense-reimbursement",
    confirmationNote:
      "Expense reimbursement policy section aligns with documented process. Concur system identified as the expense management platform.",
    sourceEvidence: [
      {
        documentName: "Corporate Policy Manual v4.2",
        quote:
          "Employee expense claims must be submitted within 60 days of the expenditure. Line manager approval is required for all claims.",
        pageRef: "p. 31",
      },
    ],
  },
  {
    processId: "proc-corp-card",
    confirmationNote:
      "Corporate card programme referenced in policy with defined reconciliation requirements and cardholder responsibilities.",
    sourceEvidence: [
      {
        documentName: "Corporate Policy Manual v4.2",
        quote:
          "Corporate cardholders must submit monthly reconciliations with supporting receipts within 15 business days of the statement date.",
        pageRef: "p. 33",
      },
    ],
  },
  {
    processId: "proc-order-fulfillment",
    confirmationNote:
      "Order-to-cash cycle described in Annual Report revenue recognition disclosures. Salesforce CPQ identified as the quoting and order entry system.",
    sourceEvidence: [
      {
        documentName: "Annual Report FY2025",
        quote:
          "Revenue from product sales is recognised at the point in time when control transfers to the customer, generally upon delivery.",
        pageRef: "Note 2(c)",
      },
    ],
  },
  {
    processId: "proc-contract-rev-rec",
    confirmationNote:
      "ASC 606 implementation details in the Annual Report confirm the five-step model is applied. RevPro system handles automated revenue allocation.",
    sourceEvidence: [
      {
        documentName: "Annual Report FY2025",
        quote:
          "The Company identifies performance obligations in its contracts and allocates the transaction price based on standalone selling prices.",
        pageRef: "Note 2(c)",
      },
      {
        documentName: "IT Systems Inventory",
        quote:
          "Zuora RevPro — revenue recognition engine integrated with Salesforce and NetSuite for ASC 606 compliance.",
        pageRef: "Row 15",
      },
    ],
  },
  {
    processId: "proc-credit-collections",
    confirmationNote:
      "Credit management and collections procedures described in the Policy Manual with defined escalation thresholds and write-off authorities.",
    sourceEvidence: [
      {
        documentName: "Corporate Policy Manual v4.2",
        quote:
          "Credit limits are reviewed annually. Write-offs exceeding $10,000 require Controller approval; amounts over $50,000 require CFO approval.",
        pageRef: "p. 37",
      },
    ],
  },
  {
    processId: "proc-journal-entry",
    confirmationNote:
      "Journal entry controls extensively documented in policy. Internal Audit plan includes quarterly testing of journal entry controls.",
    sourceEvidence: [
      {
        documentName: "Corporate Policy Manual v4.2",
        quote:
          "All manual journal entries require supporting documentation and must be approved by an individual independent of the preparer.",
        pageRef: "p. 22",
      },
      {
        documentName: "Internal Audit Plan — Q1 2026",
        quote:
          "Journal entry testing — review sample of manual entries for proper authorisation, documentation, and account coding.",
        pageRef: "Section 3.2",
      },
    ],
  },
  {
    processId: "proc-month-end-close",
    confirmationNote:
      "Month-end close calendar and reconciliation requirements documented. Five-day close target confirmed in Annual Report management commentary.",
    sourceEvidence: [
      {
        documentName: "Annual Report FY2025",
        quote:
          "The Company maintains a structured month-end close process completed within five business days, with all reconciliations subject to independent review.",
        pageRef: "MD&A, p. 18",
      },
    ],
  },
];
